import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { FileSpreadsheet, Upload, Loader2, Check, X, AlertTriangle } from 'lucide-react';

export const CsvImportModal = ({
  open,
  onOpenChange,
  csvImportType,
  csvPreview,
  csvColumnChoice,
  csvCorrections,
  csvSourceUnit,
  uploadingCsv,
  onSitesCsvImport,
  onMonthlyCsvPreview,
  onColumnChoiceChange,
  onConfirmMonthlyCsvImport,
  onCancelCsvPreview,
  onSetCsvSourceUnit,
  sitesFileInputRef,
  monthlyFileInputRef,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#397DFF]" />
            {csvImportType === 'sites' ? 'Importer les données des sites' : 'Importer les données mensuelles'}
          </DialogTitle>
          <DialogDescription>
            {csvImportType === 'sites' 
              ? 'Importez un fichier CSV pour ajouter ou mettre à jour les données de consommation par site.'
              : csvPreview 
                ? "Vérifiez les données ci-dessous avant de confirmer l'import."
                : "Importez un fichier CSV pour ajouter ou mettre à jour les données d'évolution mensuelle."
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {csvImportType === 'monthly' && csvPreview ? (
            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-[hsl(var(--emeraude)/0.1)] border border-[hsl(var(--emeraude)/0.3)] rounded-lg p-4">
                <h4 className="font-semibold text-[hsl(var(--emeraude))] mb-2 flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  Prévisualisation des données
                </h4>
                <div className="grid grid-cols-3 gap-4 text-sm text-[hsl(var(--secondary))]">
                  <div><span className="font-medium">{csvPreview.summary.months_found}</span> mois détectés</div>
                  <div>Total 2026: <span className="font-medium">{csvPreview.summary.total_current_year_mwh?.toLocaleString('fr-FR')} MWh</span></div>
                  <div>Total N-1: <span className="font-medium">{csvPreview.summary.total_previous_year_mwh?.toLocaleString('fr-FR')} MWh</span></div>
                </div>
              </div>

              {/* Correction Alert */}
              {csvCorrections.length > 0 && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-amber-800">Corrections automatiques détectées</h4>
                      <p className="text-sm text-amber-700 mt-1">
                        {csvCorrections.length} mois ont été corrigés car la valeur "Estimé" semblait anormalement basse :
                      </p>
                      <ul className="text-sm text-amber-700 mt-1 list-disc pl-5">
                        {csvCorrections.map((row, idx) => (
                          <li key={idx}><strong>{row.month}</strong> : utilisation de "Mesuré" à la place</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Column Choice Selector */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-3">Source des données année courante</h4>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { value: 'auto', label: 'Automatique', activeClass: 'bg-[#397DFF] text-white' },
                    { value: 'estimated', label: 'Estimé uniquement', activeClass: 'bg-amber-500 text-white' },
                    { value: 'measured', label: 'Mesuré uniquement', activeClass: 'bg-blue-500 text-white' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => onColumnChoiceChange(opt.value)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        csvColumnChoice === opt.value ? opt.activeClass : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                      }`}
                      disabled={uploadingCsv}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {csvColumnChoice === 'auto' && "Mode automatique : utilise 'Estimé' sauf si la valeur semble anormale."}
                  {csvColumnChoice === 'estimated' && "Utilise uniquement la colonne 'Estimé' (projections)."}
                  {csvColumnChoice === 'measured' && "Utilise uniquement la colonne 'Mesuré' quand disponible."}
                </p>
              </div>

              {/* Data Table */}
              <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Mois</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-700">Année courante (MWh)</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-700">N-1 (MWh)</th>
                      <th className="px-3 py-2 text-center font-medium text-gray-700">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvPreview.preview.map((row, idx) => (
                      <tr key={idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${row.source?.includes('corrigé') ? 'bg-amber-50' : ''}`}>
                        <td className="px-3 py-2 font-medium">{row.month}</td>
                        <td className="px-3 py-2 text-right">
                          {row.current_year_mwh?.toLocaleString('fr-FR', { minimumFractionDigits: 1 }) || '-'}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {row.previous_year_mwh?.toLocaleString('fr-FR', { minimumFractionDigits: 1 }) || '-'}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            row.source?.includes('corrigé') ? 'bg-amber-200 text-amber-800'
                              : row.source === 'mesuré' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {row.source}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={onCancelCsvPreview} className="flex-1" disabled={uploadingCsv}>
                  <X className="w-4 h-4 mr-2" />Annuler
                </Button>
                <Button onClick={onConfirmMonthlyCsvImport} className="flex-1 bg-[#397DFF] hover:bg-[#2563eb]" disabled={uploadingCsv}>
                  {uploadingCsv ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Confirmer l'import
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Format explanation */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">Format du fichier CSV</h4>
                {csvImportType === 'sites' ? (
                  <div className="text-sm text-blue-800 space-y-2">
                    <p>Le fichier doit contenir les colonnes suivantes :</p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li><strong>Colonne 1 :</strong> Nom du site (obligatoire)</li>
                      <li><strong>Colonne 2 :</strong> Consommation mois en cours</li>
                      <li><strong>Colonne 3 :</strong> Consommation même mois N-1</li>
                      <li><strong>Colonne 4 :</strong> Total année en cours</li>
                      <li><strong>Colonne 5 :</strong> Total année N-1</li>
                    </ul>
                    <p className="mt-2 text-xs">Exemple : <code className="bg-blue-100 px-1 rounded">Site A;45,5;42,3;350;340</code></p>
                  </div>
                ) : (
                  <div className="text-sm text-blue-800 space-y-2">
                    <p>Formats acceptés :</p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li><strong>Format simple :</strong> Mois;Année courante;Année N-1</li>
                      <li><strong>Format export énergie :</strong> Date;Estimé;Mesuré;N-1</li>
                    </ul>
                    <div className="mt-2 text-xs space-y-1">
                      <p>Exemple simple : <code className="bg-blue-100 px-1 rounded">Jan;120,5;115,3</code></p>
                      <p>Exemple export : <code className="bg-blue-100 px-1 rounded">01/01/2026;39799;1789899;1756932</code></p>
                    </div>
                  </div>
                )}
              </div>

              {/* Unit selector - Only for sites import */}
              {csvImportType === 'sites' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="unit-selector">
                  <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Unité des données du fichier CSV
                  </h4>
                  <p className="text-sm text-amber-800 mb-3">
                    Sélectionnez l'unité utilisée dans votre fichier CSV. Les données seront automatiquement converties en MWh si nécessaire.
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => onSetCsvSourceUnit('kwh')}
                      data-testid="unit-kwh-btn"
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                        csvSourceUnit === 'kwh' ? 'bg-amber-500 text-white' : 'bg-white border border-amber-300 text-amber-700 hover:bg-amber-100'
                      }`}
                      disabled={uploadingCsv}
                    >
                      kWh (kiloWattheures)
                    </button>
                    <button
                      onClick={() => onSetCsvSourceUnit('mwh')}
                      data-testid="unit-mwh-btn"
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                        csvSourceUnit === 'mwh' ? 'bg-emerald-500 text-white' : 'bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-100'
                      }`}
                      disabled={uploadingCsv}
                    >
                      MWh (MégaWattheures)
                    </button>
                  </div>
                  <p className="text-xs text-amber-700 mt-2">
                    {csvSourceUnit === 'kwh' 
                      ? "Les valeurs seront divisées par 1000 pour convertir kWh -> MWh"
                      : "Les valeurs seront importées directement en MWh (pas de conversion)"
                    }
                  </p>
                </div>
              )}

              {/* Hidden file inputs */}
              <input type="file" ref={sitesFileInputRef} accept=".csv" className="hidden" onChange={onSitesCsvImport} />
              <input type="file" ref={monthlyFileInputRef} accept=".csv" className="hidden" onChange={onMonthlyCsvPreview} />

              {/* Upload button */}
              <div className="flex flex-col items-center gap-4 py-4">
                <div 
                  className="w-full border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#397DFF] hover:bg-[#397DFF]/5 transition-colors cursor-pointer"
                  onClick={() => {
                    if (csvImportType === 'sites') sitesFileInputRef.current?.click();
                    else monthlyFileInputRef.current?.click();
                  }}
                >
                  {uploadingCsv ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="w-10 h-10 text-[#397DFF] animate-spin mb-2" />
                      <p className="text-sm text-gray-600">Chargement en cours...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Upload className="w-10 h-10 text-gray-400 mb-2" />
                      <p className="text-sm font-medium text-gray-700">Cliquez pour sélectionner un fichier CSV</p>
                      <p className="text-xs text-gray-500 mt-1">ou glissez-déposez votre fichier ici</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Info note */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <strong>Note :</strong> {csvImportType === 'sites' 
                  ? 'Les sites existants seront mis à jour si leur nom correspond. Les nouveaux sites seront ajoutés à la liste.'
                  : "Vous pourrez prévisualiser les données avant de confirmer l'import."
                }
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
